import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { PackageComponentsEditor } from '../components/PackageComponentsEditor';
import { RichTextEditor, richTextIsEmpty, sanitizeRichText } from '../components/RichTextEditor';
import { TicketPriceTable } from '../components/TicketPriceTable';
import {
  ApiError,
  PRODUCT_CATEGORIES,
  PRODUCT_CHANNELS,
  PRODUCT_CURRENCIES,
  createProduct,
  getProduct,
  updateProduct,
  type CreateProductDto,
  type ProductCategory,
  type ProductChannel,
  type ProductCurrency,
  type ProductSummary,
  type UpdateProductDto,
} from '../lib/api-client';
import { ticketTypeLabel } from '../lib/ticket-type-label';

type Locale = 'zh-TW' | 'en' | 'ja';
const LOCALES: readonly Locale[] = ['zh-TW', 'en', 'ja'] as const;
const LOCALE_TAB_LABEL: Record<Locale, string> = { 'zh-TW': '繁中', en: 'EN', ja: '日本語' };

const CATEGORY_LABEL: Record<ProductCategory, string> = {
  trip: '行程（trip）',
  experience: '體驗（experience）',
  package: '套票（package）',
  charter: '包船（charter）',
};

const CHANNEL_LABEL: Record<ProductChannel, string> = {
  web: 'Web',
  kiosk: 'Kiosk',
  partner: 'Partner',
};

interface FormState {
  sku: string;
  category: ProductCategory;
  nameByLocale: Record<Locale, string>;
  descByLocale: Record<Locale, string>;
  basePrice: string; // 用字串避免 number 空字串轉 0；= 成人原價
  /** 非成人票種原價（key=票種, value=價格字串）；有 entry = 該票種可售。 */
  ticketPrices: Record<string, string>;
  /** 住客成人原價；空字串 = 未設住客價（與非住客同價）。 */
  guestBasePrice: string;
  guestTicketPrices: Record<string, string>;
  currency: ProductCurrency;
  durationMinutes: string;
  channels: ProductChannel[];
  isPublished: boolean;
}

const EMPTY_FORM: FormState = {
  sku: '',
  category: 'trip',
  nameByLocale: { 'zh-TW': '', en: '', ja: '' },
  descByLocale: { 'zh-TW': '', en: '', ja: '' },
  basePrice: '',
  ticketPrices: {},
  guestBasePrice: '',
  guestTicketPrices: {},
  currency: 'TWD',
  durationMinutes: '',
  channels: [...PRODUCT_CHANNELS],
  isPublished: false,
};

function recordToLocaleMap(src: Record<string, string> | null | undefined): Record<Locale, string> {
  const out: Record<Locale, string> = { 'zh-TW': '', en: '', ja: '' };
  if (!src) return out;
  for (const loc of LOCALES) {
    if (typeof src[loc] === 'string') out[loc] = src[loc];
  }
  return out;
}

function fromProduct(p: ProductSummary): FormState {
  // 過濾 BE 可能回傳之非 prompt 範圍 channel（如 floor），UI 不展示
  const safeChannels = p.channels.filter((c): c is ProductChannel =>
    (PRODUCT_CHANNELS as readonly string[]).includes(c),
  );
  const currency = (PRODUCT_CURRENCIES as readonly string[]).includes(p.currency)
    ? (p.currency as ProductCurrency)
    : 'TWD';
  return {
    sku: p.sku,
    category: p.category,
    nameByLocale: recordToLocaleMap(p.name_i18n),
    descByLocale: recordToLocaleMap(p.description_i18n),
    basePrice: p.base_price,
    ticketPrices: p.ticket_prices
      ? Object.fromEntries(Object.entries(p.ticket_prices).map(([k, v]) => [k, String(v)]))
      : {},
    guestBasePrice: p.guest_base_price != null ? String(p.guest_base_price) : '',
    guestTicketPrices: p.guest_ticket_prices
      ? Object.fromEntries(Object.entries(p.guest_ticket_prices).map(([k, v]) => [k, String(v)]))
      : {},
    currency,
    durationMinutes: p.duration_minutes != null ? String(p.duration_minutes) : '',
    channels: safeChannels.length > 0 ? safeChannels : [...PRODUCT_CHANNELS],
    isPublished: p.is_published,
  };
}

/**
 * FormState.ticketPrices（字串）→ 後端 ticket_prices（number map）。
 * 只收有填且合法（>= 0）之非成人票種；回 { map, error }。
 */
function buildTicketPrices(rec: Record<string, string>): {
  map: Record<string, number>;
  error?: string;
} {
  const map: Record<string, number> = {};
  for (const [type, raw] of Object.entries(rec)) {
    const s = raw.trim();
    if (s === '') continue;
    const n = Number(s);
    if (!Number.isFinite(n) || n < 0) {
      return { map: {}, error: `「${ticketTypeLabel(type)}」原價須為 >= 0 之數值` };
    }
    map[type] = n;
  }
  return { map };
}

function buildI18n(map: Record<Locale, string>): Record<string, string> | undefined {
  const out: Record<string, string> = {};
  for (const loc of LOCALES) {
    const v = map[loc].trim();
    if (v) out[loc] = v;
  }
  return Object.keys(out).length === 0 ? undefined : out;
}

/** 描述（HTML）：去「視覺為空」者，留存前再淨化一次（防直接寫入之惡意 HTML）。 */
function buildDescI18n(map: Record<Locale, string>): Record<string, string> | undefined {
  const out: Record<string, string> = {};
  for (const loc of LOCALES) {
    if (!richTextIsEmpty(map[loc])) out[loc] = sanitizeRichText(map[loc]);
  }
  return Object.keys(out).length === 0 ? undefined : out;
}

interface ValidationResult {
  errors: string[];
  createDto?: CreateProductDto;
  updateDto?: UpdateProductDto;
}

function validate(form: FormState, isEdit: boolean): ValidationResult {
  const errors: string[] = [];

  if (!isEdit) {
    const sku = form.sku.trim();
    if (sku.length < 1 || sku.length > 64) {
      errors.push('SKU 必填，長度 1..64');
    } else if (!/^[A-Za-z0-9][A-Za-z0-9_-]*$/.test(sku)) {
      errors.push('SKU 僅允許英數及 _ -，且首字需為英數');
    }
  }

  const nameI18n = buildI18n(form.nameByLocale);
  if (!nameI18n || !nameI18n['zh-TW']) {
    errors.push('名稱（zh-TW）必填');
  }

  const priceNum = Number(form.basePrice);
  if (form.basePrice.trim() === '' || !Number.isFinite(priceNum) || priceNum < 0) {
    errors.push('售價必填，需為 ≥ 0 之數值');
  }

  let durationNum: number | undefined;
  if (form.durationMinutes.trim() !== '') {
    const n = Number(form.durationMinutes);
    if (!Number.isInteger(n) || n < 0) {
      errors.push('時長需為非負整數（分）');
    } else {
      durationNum = n;
    }
  }

  if (form.channels.length === 0) {
    errors.push('至少選一個通道');
  }

  const tp = buildTicketPrices(form.ticketPrices);
  if (tp.error) errors.push(tp.error);

  /*
   * 住客價是**整組**取代非住客價，不是逐欄位混用（後端 perTypeBasePrice 亦同）。
   * 所以「有住客票種價、卻沒有住客成人價」是個解不出來的狀態：那些票種永遠不會
   * 被選用。與其安靜地忽略，不如在這裡擋下來。
   */
  const gtp = buildTicketPrices(form.guestTicketPrices);
  if (gtp.error) errors.push(`住客價：${gtp.error}`);
  const guestBaseRaw = form.guestBasePrice.trim();
  const guestBaseNum = guestBaseRaw === '' ? undefined : Number(guestBaseRaw);
  if (guestBaseRaw !== '' && (!Number.isFinite(guestBaseNum) || (guestBaseNum as number) < 0)) {
    errors.push('住客成人原價需為 ≥ 0 之數值');
  }
  if (guestBaseRaw === '' && Object.keys(gtp.map).length > 0) {
    errors.push(
      '已填住客票種價，請一併填「住客成人原價」（住客價整組生效，缺成人價則整組不會被選用）',
    );
  }

  if (errors.length > 0 || !nameI18n) {
    return { errors };
  }

  const descI18n = buildDescI18n(form.descByLocale);

  if (isEdit) {
    const updateDto: UpdateProductDto = {
      category: form.category,
      name_i18n: nameI18n,
      base_price: priceNum,
      // 永遠送（含 {}）以允許清空非成人票種
      ticket_prices: tp.map,
      // 住客價同理：整組送，空字串代表「取消住客價、與非住客同價」
      guest_base_price: guestBaseNum ?? null,
      guest_ticket_prices: gtp.map,
      currency: form.currency,
      channels: form.channels,
      is_published: form.isPublished,
    };
    if (descI18n) updateDto.description_i18n = descI18n;
    if (durationNum !== undefined) updateDto.duration_minutes = durationNum;
    return { errors: [], updateDto };
  }

  const createDto: CreateProductDto = {
    sku: form.sku.trim(),
    category: form.category,
    name_i18n: nameI18n,
    base_price: priceNum,
    currency: form.currency,
    channels: form.channels,
  };
  if (Object.keys(tp.map).length > 0) createDto.ticket_prices = tp.map;
  if (guestBaseNum !== undefined) {
    createDto.guest_base_price = guestBaseNum;
    if (Object.keys(gtp.map).length > 0) createDto.guest_ticket_prices = gtp.map;
  }
  if (descI18n) createDto.description_i18n = descI18n;
  if (durationNum !== undefined) createDto.duration_minutes = durationNum;
  return { errors: [], createDto };
}

function toErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    return err.problem?.detail ?? err.problem?.title ?? err.message;
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

export function ProductEditPage(): JSX.Element {
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [localErrors, setLocalErrors] = useState<string[]>([]);
  const [activeLocale, setActiveLocale] = useState<Locale>('zh-TW');

  const localeHasContent = (loc: Locale): boolean =>
    form.nameByLocale[loc].trim().length > 0 || !richTextIsEmpty(form.descByLocale[loc]);

  const productQuery = useQuery({
    queryKey: ['products.get', id],
    queryFn: () => getProduct(id as string),
    enabled: isEdit,
  });

  useEffect(() => {
    if (productQuery.data) {
      setForm(fromProduct(productQuery.data));
    }
  }, [productQuery.data]);

  const createMut = useMutation({
    mutationFn: (dto: CreateProductDto) => createProduct(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products.list'] });
      navigate('/products');
    },
  });

  const updateMut = useMutation({
    mutationFn: (dto: UpdateProductDto) => updateProduct(id as string, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products.list'] });
      queryClient.invalidateQueries({ queryKey: ['products.get', id] });
      navigate('/products');
    },
  });

  const submitting = createMut.isPending || updateMut.isPending;
  /*
   * 有沒有住客價，看的是「住客成人原價有沒有填」——與後端 `perTypeBasePrice`
   * 的判定一致（`guest_base_price` 為 NULL 就整組退回非住客價）。不另存一個
   * 開關 state，免得它與實際資料說法不一。
   */
  const guestEnabled = form.guestBasePrice.trim() !== '';
  const apiError = useMemo(
    () => createMut.error ?? updateMut.error ?? null,
    [createMut.error, updateMut.error],
  );

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    const v = validate(form, isEdit);
    if (v.errors.length > 0) {
      setLocalErrors(v.errors);
      return;
    }
    setLocalErrors([]);
    if (isEdit && v.updateDto) {
      updateMut.mutate(v.updateDto);
    } else if (!isEdit && v.createDto) {
      createMut.mutate(v.createDto);
    }
  }

  function toggleChannel(ch: ProductChannel): void {
    setForm((prev) => {
      const has = prev.channels.includes(ch);
      const next = has ? prev.channels.filter((c) => c !== ch) : [...prev.channels, ch];
      return { ...prev, channels: next };
    });
  }

  if (isEdit && productQuery.isLoading) {
    return <div>載入中…</div>;
  }
  if (isEdit && productQuery.error) {
    return (
      <div>
        <h1 className="page-head__title">編輯商品</h1>
        <div className="error-banner">{toErrorMessage(productQuery.error)}</div>
        <button onClick={() => navigate('/products')}>返回列表</button>
      </div>
    );
  }

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">{isEdit ? '編輯商品' : '新增商品'}</h1>
        <div className="page-head__actions">
          <button onClick={() => navigate('/products')} disabled={submitting}>
            返回列表
          </button>
        </div>
      </div>

      {localErrors.length > 0 && (
        <div className="error-banner">
          <strong>表單檢核錯誤：</strong>
          <ul style={{ margin: '6px 0 0 18px' }}>
            {localErrors.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </div>
      )}
      {apiError && <div className="error-banner">{toErrorMessage(apiError)}</div>}

      <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 12, maxWidth: 720 }}>
        <Field label="SKU">
          <input
            type="text"
            value={form.sku}
            readOnly={isEdit}
            disabled={isEdit}
            maxLength={64}
            required={!isEdit}
            onChange={(e) => setForm((p) => ({ ...p, sku: e.target.value }))}
            style={{ width: '100%' }}
          />
          {isEdit && (
            <small style={{ color: '#6b7280' }}>SKU 不可變更（避免歷史訂單 join 漂移）</small>
          )}
        </Field>

        <Field label="類別">
          <select
            value={form.category}
            onChange={(e) =>
              setForm((p) => ({ ...p, category: e.target.value as ProductCategory }))
            }
            style={{ width: '100%' }}
          >
            {PRODUCT_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {CATEGORY_LABEL[c]}
              </option>
            ))}
          </select>
        </Field>

        <fieldset style={{ border: '1px solid #e5e7eb', borderRadius: 4, padding: 12 }}>
          <legend style={{ fontWeight: 600, padding: '0 6px' }}>名稱 / 描述（多語）</legend>

          {/* 語言頁籤 */}
          <div
            style={{ display: 'flex', gap: 4, marginBottom: 12, borderBottom: '1px solid #e5e7eb' }}
          >
            {LOCALES.map((loc) => {
              const active = loc === activeLocale;
              return (
                <button
                  key={loc}
                  type="button"
                  onClick={() => setActiveLocale(loc)}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
                    background: 'none',
                    color: active ? '#2563eb' : '#6b7280',
                    fontWeight: active ? 600 : 400,
                    cursor: 'pointer',
                  }}
                >
                  {LOCALE_TAB_LABEL[loc]}
                  {localeHasContent(loc) && <span style={{ color: '#10b981' }}> ●</span>}
                </button>
              );
            })}
          </div>

          <Field
            label={`名稱（${LOCALE_TAB_LABEL[activeLocale]}）${activeLocale === 'zh-TW' ? '・必填' : ''}`}
          >
            <input
              type="text"
              value={form.nameByLocale[activeLocale]}
              required={activeLocale === 'zh-TW'}
              maxLength={200}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  nameByLocale: { ...p.nameByLocale, [activeLocale]: e.target.value },
                }))
              }
              style={{ width: '100%' }}
            />
          </Field>

          <Field label={`描述（${LOCALE_TAB_LABEL[activeLocale]}）・選填`}>
            <RichTextEditor
              key={activeLocale}
              value={form.descByLocale[activeLocale]}
              onChange={(html) =>
                setForm((p) => ({
                  ...p,
                  descByLocale: { ...p.descByLocale, [activeLocale]: html },
                }))
              }
              placeholder="輸入商品介紹⋯可用上方工具列做粗體 / 清單 / 連結等排版"
            />
          </Field>
        </fieldset>

        <fieldset style={{ border: '1px solid #e5e7eb', borderRadius: 4, padding: 12 }}>
          <legend style={{ fontWeight: 600, padding: '0 6px' }}>票種票價（原價）</legend>
          <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 10px' }}>
            勾選此商品要賣的票種並填各自原價。成人必填；其他票種勾選後才會出現在購票機 / 官網。
            折扣由「定價推廣」另設，過期不會讓票種消失。
          </p>

          <TicketPriceTable
            idPrefix="tp"
            basePrice={form.basePrice}
            onBasePriceChange={(v) => setForm((p) => ({ ...p, basePrice: v }))}
            ticketPrices={form.ticketPrices}
            onTicketPricesChange={(next) => setForm((p) => ({ ...p, ticketPrices: next }))}
            adultRequired
            adultTrailing={
              <select
                value={form.currency}
                onChange={(e) =>
                  setForm((p) => ({ ...p, currency: e.target.value as ProductCurrency }))
                }
                style={{ marginLeft: 'auto' }}
              >
                {PRODUCT_CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            }
          />
        </fieldset>

        <fieldset style={{ border: '1px solid #e5e7eb', borderRadius: 4, padding: 12 }}>
          <legend style={{ fontWeight: 600, padding: '0 6px' }}>住客價（原價）</legend>
          <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 10px' }}>
            業主價目表上每個付費項目都有住客／非住客兩種價。留空 = 兩者同價。
            <br />
            <strong>住客價是整組取代，不是逐欄位混用</strong>
            ——勾了住客價之後，住客看到的可售票種也改由這張表決定（上面那張表對住客不生效）。
            身分由顧客在購票時自行宣告，系統不查證。
          </p>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 14,
              marginBottom: 10,
            }}
          >
            <input
              type="checkbox"
              checked={guestEnabled}
              onChange={() =>
                setForm((p) =>
                  guestEnabled
                    ? { ...p, guestBasePrice: '', guestTicketPrices: {} }
                    : { ...p, guestBasePrice: p.basePrice },
                )
              }
            />
            此商品另有住客價
          </label>

          <TicketPriceTable
            idPrefix="gtp"
            disabled={!guestEnabled}
            basePrice={form.guestBasePrice}
            onBasePriceChange={(v) => setForm((p) => ({ ...p, guestBasePrice: v }))}
            ticketPrices={form.guestTicketPrices}
            onTicketPricesChange={(next) => setForm((p) => ({ ...p, guestTicketPrices: next }))}
            adultRequired={guestEnabled}
            adultPlaceholder={guestEnabled ? '必填' : '（與非住客同價）'}
          />
        </fieldset>

        <Field label="時長（分；選填）">
          <input
            type="number"
            min={0}
            step="1"
            value={form.durationMinutes}
            onChange={(e) => setForm((p) => ({ ...p, durationMinutes: e.target.value }))}
            style={{ width: 200 }}
          />
        </Field>

        <Field label="販售通道">
          <div style={{ display: 'flex', gap: 16 }}>
            {PRODUCT_CHANNELS.map((ch) => (
              <label key={ch} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input
                  type="checkbox"
                  checked={form.channels.includes(ch)}
                  onChange={() => toggleChannel(ch)}
                />
                <span>{CHANNEL_LABEL[ch]}</span>
              </label>
            ))}
          </div>
        </Field>

        {isEdit && (
          <Field label="上架狀態">
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="checkbox"
                checked={form.isPublished}
                onChange={(e) => setForm((p) => ({ ...p, isPublished: e.target.checked }))}
              />
              <span>{form.isPublished ? '已上架（is_published = true）' : '草稿（未上架）'}</span>
            </label>
            <small style={{ color: '#6b7280' }}>
              切換上下架走獨立 endpoint（/products/:id/publish | /unpublish），會同步清排程旗標。
            </small>
          </Field>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button type="submit" className="primary" disabled={submitting}>
            {submitting ? '送出中…' : isEdit ? '儲存變更' : '建立商品'}
          </button>
          <button type="button" onClick={() => navigate('/products')} disabled={submitting}>
            取消
          </button>
        </div>
      </form>

      {/*
       * 套票組成放在表單之外、且只在編輯既有商品時出現：它走的是獨立的
       * PUT /products/:id/components，與商品欄位分開存；新增商品時還沒有 id 可掛。
       */}
      {isEdit && id !== undefined && form.category === 'package' && (
        <PackageComponentsEditor productId={id} />
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{label}</span>
      {children}
    </label>
  );
}
